import axios from "axios";
import { services } from "../utils/helpers";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_ADMIN_API_BASE_URL = "http://127.0.0.1:8765";


export const DB_CLIENT_VERSION_HEADER = 'x-db-client-version';
export const DB_CLIENT_VERSION_RELOAD_STORAGE_KEY = 'dbClientVersionReloadedFor';
export const ADMIN_AGENT_TOKEN_STORAGE_KEY = 'adminAgentToken';

export const normalizeAdminApiBaseUrl = (value) => {
  const trimmed = (value || '').trim();
  return trimmed.replace(/\/+$/, '');
};

export const getAdminApiBaseUrl = () => normalizeAdminApiBaseUrl(
  process.env.REACT_APP_ADMIN_API_BASE_URL || DEFAULT_ADMIN_API_BASE_URL,
);

export const buildAdminAuthHeaders = (token) => ({
  Authorization: `Bearer ${(token || '').trim()}`,
});

export const getExpectedDbClientVersion = () => process.env.REACT_APP_DB_CLIENT_VERSION || 'local';

export const isUserRouterResponse = (response) => {
  const url = response?.config?.url || '';
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/user');
  } catch (error) {
    return url.startsWith('/user');
  }
};

export const getDbClientVersionFromResponse = (response) => (
  response?.headers?.[DB_CLIENT_VERSION_HEADER] ||
  response?.headers?.[DB_CLIENT_VERSION_HEADER.toUpperCase()] ||
  response?.headers?.['X-Db-Client-Version']
);

export const hasDbClientVersionMismatch = (response) => {
  if (!isUserRouterResponse(response)) return false;
  return getDbClientVersionFromResponse(response) !== getExpectedDbClientVersion();
};

export const maybeReloadForDbClientVersionMismatch = (response, options = {}) => {
  if (!hasDbClientVersionMismatch(response)) return false;

  const reload = options.reload || (() => window.location.reload());
  const storage = options.storage || window.sessionStorage;
  const responseVersion = getDbClientVersionFromResponse(response) || 'missing';
  const reloadMarker = `${getExpectedDbClientVersion()}:${responseVersion}`;

  try {
    if (storage?.getItem(DB_CLIENT_VERSION_RELOAD_STORAGE_KEY) === reloadMarker) {
      return true;
    }
    storage?.setItem(DB_CLIENT_VERSION_RELOAD_STORAGE_KEY, reloadMarker);
  } catch (error) {
    // Reload anyway when storage is unavailable; the version check should still win.
  }

  reload();
  return true;
};

let authErrorModalCallback = null;
let isAuthErrorModalShown = false;

export const setAuthErrorHandler = (callback) => {
  authErrorModalCallback = callback;
};

export const resetAuthErrorModal = () => {
  isAuthErrorModalShown = false;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60000,
});

const adminApi = axios.create({
  baseURL: getAdminApiBaseUrl(),
  timeout: 20000,
});

api.interceptors.response.use(
  (response) => {
    maybeReloadForDbClientVersionMismatch(response);
    return response;
  },
  (error) => {
    if (error.response) {
      maybeReloadForDbClientVersionMismatch(error.response);
    }
    if (error.response && error.response.status === 401) {
      error._isAuthError = true;

      if (authErrorModalCallback && !isAuthErrorModalShown) {
        isAuthErrorModalShown = true;
        authErrorModalCallback();
      }

      return Promise.resolve({
        _isAuthError: true,
        data: { error: "Authentication error" },
      });
    }
    return Promise.reject(error);
  },
);

//// EXPERIMENTAL SECTION ////
//// EXPERIMENTAL SECTION ////
//// EXPERIMENTAL SECTION ////

export const fetchAccountsExp = async () => {
  const response = await api.get('/user/exp/accounts');
  if (response._isAuthError) return [];
  return response.data;
};

export const fetchStrategiesExp = async () => {
  const response = await api.get('/user/exp/strategies');
  if (response._isAuthError) return [];
  return response.data;
};

//// EXPERIMENTAL SECTION ////
//// EXPERIMENTAL SECTION ////
//// EXPERIMENTAL SECTION ////

export const getAuthToken = () => {
  if (api.defaults.headers.common["Authorization"] !== undefined) {
    return api.defaults.headers.common["Authorization"];
  }
};

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

export const fetchAccounts = async () => {
  const response = await api.get("/user/accounts");
  if (response._isAuthError) return [];
  return response.data;
};

export const fetchAssets = async (
  account,
  broker,
  includeSynthetic = false,
) => {
  const response = await api.get(`/user/fetch-assets`, {
    params: {
      account,
      broker,
      include_synthetic: includeSynthetic,
    },
  });
  if (response._isAuthError) return [];
  return response.data;
};

export const fetchUserProfile = async () => {
  const response = await api.get("/user/profile");
  if (response._isAuthError) return null;
  return response.data;
};

export const fetchSupportedBrokers = async () => {
  const response = await api.get("/brokers/supported");
  if (response._isAuthError) return [];
  return response.data;
};

export const linkBroker = async (broker, account, password, apiKey = null) => {
  const response = await api.post(`/brokers/link`, {
    broker: broker,
    account: account,
    password: password,
    api_key: apiKey,
  });
  if (response._isAuthError) return false;
  if (response.status !== 200) {
    return false;
  }
  return true;
};

export const unlinkBroker = async (_id) => {
  const response = await api.delete(`/brokers/unlink`, {
    params: {
      _id,
    },
  });
  if (response._isAuthError) return false;
  if (response.status !== 200) {
    return false;
  }
  return true;
};

const accountInstrumentRequest = async (action, request) => {
  try {
    const response = await api.post(`/brokers/account-instruments/${action}`, request);
    if (response._isAuthError) return { error: 'Please sign in again' };
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    return {
      error: typeof detail === 'string' ? detail : detail?.message || 'Could not update account instruments',
      reason: detail?.reason,
    };
  }
};

export const previewAccountInstruments = (request) => accountInstrumentRequest('preview', request);
export const confirmAccountInstruments = (request) => accountInstrumentRequest('confirm', request);

export const fetchMasterStatus = async (broker) => {
  const response = await api.get(`/brokers/master-status`, {
    params: {
      broker,
    },
  });
  if (response._isAuthError) return { status: false };
  return response.data;
};

export const fetchContfutOnlyStatus = async (broker) => {
  try {
    const response = await api.get(`/brokers/get-contfut-only-status/${broker}`);
    if (response._isAuthError) return false;
    return !!response.data.is_contfut_only;
  } catch (error) {
    return false;
  }
};

export const profileFetchStatistics = async (
  broker,
  account,
  symbols,
  sectypes,
  start_date,
  end_date,
  initialBalance,
) => {
  const queryParams = new URLSearchParams();
  queryParams.append("broker", broker);
  queryParams.append("account", account);

  if (Array.isArray(symbols)) {
    symbols.forEach((singleSymbol) => {
      queryParams.append("symbols", singleSymbol);
    });
  } else if (symbols) {
    queryParams.append("symbols", symbols);
  }

  if (Array.isArray(sectypes)) {
    sectypes.forEach((singleSectype) => {
      if (singleSectype) {
        queryParams.append("sectypes", singleSectype);
      }
    });
  } else if (sectypes) {
    queryParams.append("sectype", sectypes);
  }

  if (start_date) queryParams.append("start_date", start_date);
  if (end_date) queryParams.append("end_date", end_date);

  if (initialBalance !== undefined && initialBalance !== null) {
    queryParams.append("initial_balance", initialBalance);
  }

  try {
    const response = await api.get(
      `/user/fetch-profileStatistics?${queryParams.toString()}`,
    );

    return response.data;
  } catch (error) {
    console.error("Error in profileFetchStatistics API call:", error);

    throw error;
  }
};

export const fetchStatistics = async (
  broker,
  account,
  symbol,
  sectype,
  start_date,
  end_date,
) => {
  const response = await api.get("/user/fetch-statistics", {
    params: { broker, account, symbol, sectype, start_date, end_date },
  });
  if (response._isAuthError) return [];
  return response.data;
};

export const fetchData = async (broker, account, symbol, sectype) => {
  const response = await api.get("/user/fetch-data", {
    params: { broker, account, symbol, sectype },
  });
  if (response._isAuthError) return { raw_data: [] };
  return response.data;
};

export const changeActiveSource = async (
  sourceName,
  symbol,
  dest,
  timeframe,
  strategy,
  account = null,
  sectype = null,
  is_virtual = false,
) => {
  try {
    let url, data;
    switch (dest) {
      case "okx":
        url = "/user/change-active-source-crypto";
        data = {
          symbol: symbol,
          broker: dest,
          name: sourceName,
          account: account,
          timeframe: timeframe,
          strategy: strategy,
        };
        break;
      case "ib":
      case "ig":
        url = "/user/change-active-source-sec";
        data = {
          symbol: symbol,
          broker: dest,
          sectype,
          name: sourceName,
          account: account,
          timeframe: timeframe,
          strategy: strategy,
          is_virtual: is_virtual,
        };
        break;
      default:
        throw new Error(`Unsupported broker: ${dest}`);
    }

    const response = await api.put(url, data);
    if (response._isAuthError) return null;
    return response.data.now_active;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT backend: ${error}`);
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const toggleSide = async (
  sourceName,
  symbol,
  dest,
  timeframe,
  account = null,
  sectype = null,
) => {
  try {
    let url, data;
    switch (dest) {
      case "ib":
        url = "/user/toggle-side-sec";
        data = {
          name: sourceName,
          symbol: symbol,
          broker: dest,
          sectype: sectype,
          account: account,
          timeframe: timeframe,
        };
        break;
      case "ig":
        url = "/user/toggle-side-sec";
        data = {
          epic: symbol,
          broker: dest,
          sectype: sectype,
          account: account,
        };
        break;
      case "okx":
        url = "/user/toggle-side-crypto";
        data = {
          name: sourceName,
          symbol: symbol,
          broker: dest,
          account: account,
          timeframe: timeframe,
        };
        break;
      default:
        throw new Error(`Unsupported broker: ${dest}`);
    }
    const response = await api.put(url, data, { timeout: 10000 });
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const toggleSpinDown = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const url = "/user/toggle-spin-down";
    const data = {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    };

    const response = await api.put(url, data, { timeout: 10000 });
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

// Per-instrument "trading" flag: ON = MTMS-driven, OFF = scanner-driven.
export const toggleTradingInstrument = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const response = await api.put("/user/toggle-trading-instrument", {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 10000 });
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

// SOLO abort: every active postbox order of the instrument is handed to the
// operator, nothing more is traded for them. Returns {aborted} or null.
export const abortPostboxInstrument = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const response = await api.post("/user/postbox-abort-instrument", {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 10000 });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const getTradingInstrument = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const response = await api.post("/user/get-trading-instrument", {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 10000 });
    if (response._isAuthError) return null;
    return response.data.status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

// Per-instrument AUTO/SEMI trading mode: true = AUTO (TE "in play" trades), false = SEMI.
const tradingAutoRequest = async (method, path, symbol, dest, account, sectype) => {
  try {
    const response = await api[method](path, {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 10000 });
    if (response._isAuthError) return null;
    return method === 'put' ? response.data.new_status : response.data.status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};
export const toggleTradingAuto = (symbol, dest, account = null, sectype = null) =>
  tradingAutoRequest('put', '/user/toggle-trading-auto', symbol, dest, account, sectype);
export const getTradingAuto = (symbol, dest, account = null, sectype = null) =>
  tradingAutoRequest('post', '/user/get-trading-auto', symbol, dest, account, sectype);

// Wave notifications: which waves are announced. Cycles the filter of the instrument's current mode (TRADING/SCANNER).
export const cycleScannerWaveFilter = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const response = await api.put("/user/cycle-scanner-wave-filter", {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 10000 });
    if (response._isAuthError) return null;
    return { mode: response.data.mode, tradingMode: response.data.trading_mode || 'off' };
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const getScannerWaveFilter = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const response = await api.post("/user/get-scanner-wave-filter", {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 10000 });
    if (response._isAuthError) return null;
    return toWaveFilter(response.data);
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

// Server shape of a wave filter (get-scanner-wave-filter, fetch-assets rows) -> client shape.
export const toWaveFilter = (data) => ({
  mode: data.mode,
  tradingMode: data.trading_mode || 'off',
});

// Manual-settings sync across every account carrying this instrument. The switch
// is shared by the whole set: turning it off from any panel turns it off for all.
export const toggleInstrumentSync = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const response = await api.put("/user/toggle-instrument-sync", {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 30000 });
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const getInstrumentSync = async (
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const response = await api.post("/user/get-instrument-sync", {
      symbol: symbol,
      broker: dest,
      sectype: sectype,
      account: account,
    }, { timeout: 10000 });
    if (response._isAuthError) return null;
    return response.data.status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const updateAssetConfig = async (
  symbol,
  dest,
  basePosition,
  step,
  timeframe,
  strategy,
  account = null,
  sectype = null,
  leverage = null,
) => {
  try {
    let data;
    data = {
      base_position: basePosition,
      step: step,
      active_timeframe: timeframe,
      strategy: strategy,
    };
    if (leverage !== null) {
      data.leverage = leverage;
    }
    data.asset = {
      symbol: symbol,
      broker: dest,
      account: account,
      sectype: sectype,
    };

    const response = await api.put("/user/update-asset-config", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data.new_config;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setLeverage = async (
  symbol,
  dest,
  timeframe,
  strategy,
  account,
  leverage,
) => {
  try {
    let data;
    data = {
      leverage: leverage,
      active_timeframe: timeframe,
      strategy: strategy,
    };
    data.asset = {
      symbol: symbol,
      broker: dest,
      account: account,
    };

    const response = await api.put("/user/set-leverage", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data.new_config;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setActiveTimeframe = async (
  symbol,
  dest,
  timeframe,
  strategy,
  account = null,
  sectype = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: dest,
      account: account,
      sectype: sectype,
      timeframe: timeframe,
      strategy: strategy,
    };

    const response = await api.post("/user/set-active-timeframe", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data.new_config;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setSyntheticTROS = async (
  symbol,
  broker,
  tros_stage,
  upper_bound,
  lower_bound,
  tros_side,
  timeframe,
  account,
  strategy,
  sectype,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      tros_stage: tros_stage,
      upper_bound: upper_bound,
      lower_bound: lower_bound,
      tros_side: tros_side,
      timeframe: timeframe,
      account: account,
      strategy: strategy,
      sectype: sectype,
    };

    const response = await api.post("/user/set-synthetic-tros", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setSTS = async (
  symbol,
  broker,
  updown_target,
  fade_target,
  upper_bound,
  lower_bound,
  j2s_target,
  j2f_target,
  shabbat,
  fade_shabbat,
  account,
  strategy,
  sectype,
  timeframe,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      updown_target: updown_target,
      fade_target: fade_target,
      upper_bound: upper_bound,
      lower_bound: lower_bound,
      j2s_target: j2s_target,
      j2f_target: j2f_target,
      shabbat: shabbat,
      fade_shabbat: fade_shabbat,
      account: account,
      strategy: strategy,
      sectype: sectype,
      timeframe: timeframe,
    };
    console.log(data);
    const response = await api.put("/user/set-short-term-strategy", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setPatch = async (
  symbol,
  broker,
  account,
  strategy,
  active,
  local_symbol,
  multiplier,
  // The override's whole point is to replace a continuous future with a pinned
  // contract, so the patched sectype is always FUT regardless of the instrument.
  sectype = "FUT",
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      strategy: strategy,
      active: active,
      local_symbol: local_symbol,
      multiplier: multiplier,
      sectype: "FUT",
    };
    const response = await api.post("/user/set-instrument-patch", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setCounterState = async (
  symbol,
  broker,
  account,
  sectype,
  counter_state,
  group,
  level,
  strategy,
  viewMode,
  // TOS: set the wave of this specific display timeframe, not the active binding.
  targetTimeframe = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      sectype: sectype,
      group: group,
      view: viewMode,
      strategy: strategy,
      target_timeframe: targetTimeframe,
    };
    switch (level) {
      case "tactical":
        data.counter_state_tactical = counter_state;
        break;
      case "optimal":
        data.counter_state_optimal = counter_state;
        break;
      case "strategic":
        data.counter_state_strategic = counter_state;
        break;
      default:
        console.log(
          `setCounterState: No level was passed or an unknown one was passed instead. Passed level: ${level}`,
        );
        return null;
    }

    const response = await api.post("/user/set-counter-value", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

// Manual Trend Extension phase for a timeframe pair (junior -> senior).
export const setTrendExtension = async ({
  symbol, broker, account, sectype, strategy = "tos", group, view, juniorTimeframe, seniorTimeframe, phase,
}) => {
  try {
    const response = await api.post(
      "/user/set-trend-extension",
      {
        symbol, broker, account, sectype, strategy, group, view,
        junior_timeframe: juniorTimeframe, senior_timeframe: seniorTimeframe, phase,
      },
      { timeout: 10000 },
    );
    return response.data;
  } catch (error) {
    console.error(`setTrendExtension failed: ${error}`);
    return { error: requestErrorText(error) };
  }
};

// Why a request failed, in words the operator can act on: the backend's own reason
// (JSONResponse {message} or HTTPException {detail}), else the HTTP status, else
// the transport error (timeout, network).
export const requestErrorText = (error) => {
  const data = error?.response?.data;
  const reason = data?.message || (typeof data?.detail === 'string' ? data.detail : null);
  if (reason) return reason;
  if (error?.response?.status) return `HTTP ${error.response.status}`;
  return error?.message || 'unknown error';
};

export const setInfluenceArrows = async ({
  symbol,
  broker,
  account,
  sectype,
  strategy = "tos",
  group,
  view,
  // Conjugacy is per pair: this tile's timeframe against one of its ×4 seniors.
  junior_timeframe,
  senior_timeframe,
  status,
  arrows,
  counter,
  series_alive,
  swapped = false,
}) => {
  try {
    const response = await api.post(
      "/user/set-influence-arrows",
      {
        symbol, broker, account, sectype, strategy, group, view,
        junior_timeframe, senior_timeframe,
        status, arrows, counter, series_alive, swapped,
      },
      { timeout: 10000 },
    );
    return response.data;
  } catch (error) {
    console.error(`setInfluenceArrows failed: ${error}`);
    return { error: requestErrorText(error) };
  }
};

export const fetchSupercycleStats = async ({
  broker,
  symbol,
  account,
  strategy = "tos",
  view = "solo",
}) => {
  const response = await api.get("/user/fetch-supercycle-stats", {
    params: { broker, symbol, account, strategy, view },
  });
  if (response._isAuthError) return { rows: [] };
  return response.data;
};

export const resetSupercycleStats = async ({
  broker,
  symbol,
  account,
  strategy = "tos",
  view = "solo",
  junior_timeframe,
  senior_timeframe,
}) => {
  try {
    const response = await api.post(
      "/user/reset-supercycle-stats",
      { broker, symbol, account, strategy, view, junior_timeframe, senior_timeframe },
      { timeout: 10000 },
    );
    return response.data;
  } catch (error) {
    console.error(`resetSupercycleStats failed: ${error}`);
    return null;
  }
};

export const resetLastTrainStats = async ({
  broker,
  symbol,
  account,
  strategy = "tos",
  view = "solo",
  junior_timeframe,
  senior_timeframe,
}) => {
  try {
    const response = await api.post(
      "/user/reset-last-train-stats",
      { broker, symbol, account, strategy, view, junior_timeframe, senior_timeframe },
      { timeout: 10000 },
    );
    return response.data;
  } catch (error) {
    console.error(`resetLastTrainStats failed: ${error}`);
    return null;
  }
};

export const setCounterSide = async (
  symbol,
  broker,
  account,
  sectype,
  counter_side,
  group,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      sectype: sectype,
      counter_side: counter_side,
      group: group,
    };

    const response = await api.post("/user/set-counter-side", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setTradingBound = async (
  symbol,
  broker,
  account,
  strategy,
  sectype,
  bound_name,
  bound_value,
  group,
  view,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      strategy: strategy,
      sectype: sectype,
      bound_name: bound_name,
      bound_value: bound_value,
      group: group,
      view: view,
    };

    const response = await api.post("/user/set-trading-bound", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setTradableWaves = async (
  symbol,
  broker,
  account,
  strategy,
  sectype,
  waves_du4,
  waves_du5,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      strategy: strategy,
      sectype: sectype,
      waves_du4: waves_du4,
      waves_du5: waves_du5,
    };

    const response = await api.post("/user/set-waves-patch", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setTOSPatch = async (
  symbol,
  broker,
  account,
  strategy,
  sectype,
  timeframe,
  tactical,
  optimal,
  strategic,
  // Timeframe map, e.g. { tactical_timeframe: "16m" }. Only the keys passed here
  // are sent; the backend leaves omitted bindings untouched, so panels that do
  // not manage the map cannot wipe it.
  bindings = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      strategy: strategy,
      sectype: sectype,
      timeframe: timeframe,
      tactical: tactical,
      optimal: optimal,
      strategic: strategic,
      ...(bindings || {}),
    };

    const response = await api.post("/user/set-tos-patch", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setTOSQuantity = async (
  symbol,
  broker,
  account,
  sectype,
  strategy,
  timeframe,
  level,
  quantity,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      strategy: strategy,
      sectype: sectype,
      timeframe: timeframe,
      level: level,
      quantity: quantity,
    };
    const response = await api.post("/user/set-tos-quantity", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const toggleTOSShabbat = async (
  symbol,
  broker,
  account,
  strategy,
  sectype,
  timeframe,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      strategy: strategy,
      sectype: sectype,
      timeframe: timeframe,
    };
    const response = await api.post("/user/toggle-tos-shabbat", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setDjangoUnchainedTOS = async (
  symbol,
  broker,
  account,
  sectype,
  timeframe,
  duConfig,
) => {
  try {
    const serializeLevelConfig = (
      levelConfig = {},
      { allowParity = false } = {},
    ) => {
      const wavePairs = levelConfig.wavePairs || {};
      const persistentWavePairs = levelConfig.persistentWavePairs || {};
      return {
        parity_mode: allowParity ? levelConfig.parityMode || "all" : "all",
        close_mode: levelConfig.closeMode || false,
        d1: wavePairs.d1 || false,
        u1: wavePairs.u1 || false,
        d2: wavePairs.d2 || false,
        u2: wavePairs.u2 || false,
        d3: wavePairs.d3 || false,
        u3: wavePairs.u3 || false,
        d4: wavePairs.d4 || false,
        u4: wavePairs.u4 || false,
        d5: wavePairs.d5 || false,
        u5: wavePairs.u5 || false,
        pd1: persistentWavePairs.d1 || false,
        pu1: persistentWavePairs.u1 || false,
        pd2: persistentWavePairs.d2 || false,
        pu2: persistentWavePairs.u2 || false,
        pd3: persistentWavePairs.d3 || false,
        pu3: persistentWavePairs.u3 || false,
        pd4: persistentWavePairs.d4 || false,
        pu4: persistentWavePairs.u4 || false,
        pd5: persistentWavePairs.d5 || false,
        pu5: persistentWavePairs.u5 || false,
      };
    };

    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      sectype: sectype,
      timeframe: timeframe,
      tactical: serializeLevelConfig(duConfig?.tactical, { allowParity: true }),
      optimal: serializeLevelConfig(duConfig?.optimal, { allowParity: true }),
      strategic: serializeLevelConfig(duConfig?.strategic, { allowParity: true }),
    };
    const response = await api.post("/user/set-django-unchained-tos", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setModuleState = async (
  symbol,
  broker,
  account,
  strategy,
  module,
  state,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      strategy: strategy,
      module: module.toLowerCase(),
      state: state,
    };

    const response = await api.post("/user/set-module-patch", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const toggleCounterHidden = async (symbol, broker, account, group) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      group: group,
    };

    const response = await api.post("/user/toggle-group-hidden", data, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setFade = async (
  status,
  symbol,
  dest,
  strategy,
  account = null,
  sectype = "",
  timeframe = "",
) => {
  try {
    const response = await api.post("/user/set-fade", {
      status: status,
      symbol: symbol,
      broker: dest,
      strategy: strategy,
      sectype: sectype,
      account: account,
      timeframe: timeframe,
    });
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DB_CLIENT: ${error}`);
    return null;
  }
};

export const toggleMaster = async (
  symbol,
  dest,
  strategy,
  account = null,
  sectype = "",
  timeframe = "",
) => {
  try {
    let url, data;
    switch (dest) {
      case "okx":
        url = "/user/toggle-crypto-algo";
        data = { symbol, broker: dest, strategy, account, timeframe };
        break;
      case "ib":
        url = "/user/toggle-sec-algo";
        data = { symbol, broker: dest, strategy, sectype, account, timeframe };
        break;
      case "ig":
        url = "/user/toggle-sec-algo";
        data = { epic: symbol, broker: dest };
        break;
      default:
        throw new Error(`Unsupported broker: ${dest}`);
    }

    const response = await api.put(url, data);
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(`Exception: Cannot connect to DB_CLIENT: ${error}`);
    return null;
  }
};

export const toggleFestival = async (
  symbol,
  dest,
  account = null,
  sectype = "",
) => {
  try {
    let url, data;

    switch (dest) {
      case "okx":
        url = "/user/toggle-festival-crypto";
        data = {
          symbol: symbol,
          broker: dest,
        };
        break;
      case "ib":
        url = "/user/toggle-festival-sec";
        data = {
          symbol: symbol,
          broker: dest,
          sectype: sectype,
          account: account,
        };
        break;
      case "ig":
        url = "/user/toggle-festival-sec";
        data = {
          epic: symbol,
          broker: dest,
        };
        break;
      default:
        console.error(`Unsupported broker: ${dest}`);
        return null;
    }

    const response = await api.put(url, data);
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(
      `Error toggling festival for symbol: ${symbol}, broker: ${dest}`,
      error,
    );
    return null;
  }
};

export const toggleOffensive = async (
  symbol,
  dest,
  account = null,
  sectype = "",
) => {
  try {
    let url, data;

    switch (dest) {
      case "okx":
        url = "/user/toggle-offensive-crypto";
        data = {
          symbol: symbol,
          broker: dest,
        };
        break;
      case "ib":
        url = "/user/toggle-offensive-sec";
        data = {
          symbol: symbol,
          broker: dest,
          sectype: sectype,
          account: account,
        };
        break;
      case "ig":
        url = "/user/toggle-offensive-sec";
        data = {
          epic: symbol,
          broker: dest,
        };
        break;
      default:
        console.error(`Unsupported broker: ${dest}`);
        return null;
    }

    const response = await api.put(url, data);
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(
      `Error toggling offensive for symbol: ${symbol}, broker: ${dest}`,
      error,
    );
    return null;
  }
};

export const switchMaster = async (dest) => {
  let url, data;

  try {
    switch (dest) {
      case "okx":
        url = "/user/toggle-broker";
        data = { name: dest };
        break;
      case "ib":
        url = "/user/toggle-broker";
        data = { name: dest };
        break;
      default:
        console.error(`Unsupported broker: ${dest}`);
        return null;
    }

    const response = await api.put(url, data, { timeout: 10000 });
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error(`Error switching master for broker: ${dest}`, error);
    return null;
  }
};

export const toggleActive = async (
  symbol,
  broker,
  account = "default",
  sectype = "",
) => {
  try {
    const data = { broker };
    if (broker === "ig") {
      data.epic = symbol;
    } else {
      data.symbol = symbol;
    }
    data.account = account;
    if (sectype) data.sectype = sectype;
    const response = await api.put("/user/toggle-active", data);
    if (response._isAuthError) return null;
    return response.data.new_status;
  } catch (error) {
    console.error("Error toggling active status:", error);
    return null;
  }
};

export const cleanupTosActiveState = async (
  symbol,
  broker,
  account,
  sectype = "",
  timeframe,
) => {
  try {
    const data = {
      symbol,
      broker,
      account,
      strategy: "tos",
      timeframe,
    };
    if (sectype) data.sectype = sectype;

    const response = await api.post("/user/cleanup-tos-active-state", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error cleaning up TOS active state:", error);
    return null;
  }
};

export const fetchDeepAssets = async (broker, account) => {
  try {
    const params = { broker, account };
    const response = await api.get("/user/deep-fetch-assets", {
      params,
    });
    if (response._isAuthError) return [];
    return response.data;
  } catch (error) {
    console.error("Error fetching assets:", error);
    throw error;
  }
};

export const sendHealthChecks = async () => {
  const statusResults = [];

  try {
    for (const service of services) {
      try {
        const response = await api.get(service.url, { timeout: 1000 });
        if (response._isAuthError) {
          statusResults.push({
            name: service.name,
            status: "Unhealthy",
            errorCode: "Authentication Error",
          });
          continue;
        }
        if (response.status === 200) {
          statusResults.push({
            name: service.name,
            status: "Healthy",
            errorCode: null,
          });
        } else {
          statusResults.push({
            name: service.name,
            status: "Unhealthy",
            errorCode: `Status code ${response.status}`,
          });
        }
      } catch (err) {
        statusResults.push({
          name: service.name,
          status: "Unhealthy",
          errorCode: err.message,
        });
      }
    }
    return { success: true, message: "Success", statuses: statusResults };
  } catch (err) {
    return {
      success: false,
      message: `Failed to load: ${err.message}`,
      statuses: statusResults,
    };
  }
};

export const fetchAdminContainers = async (token) => {
  const response = await adminApi.get("/containers", {
    headers: buildAdminAuthHeaders(token),
  });
  return response.data;
};

export const fetchAdminServiceAliases = async (token) => {
  const response = await adminApi.get("/service-aliases", {
    headers: buildAdminAuthHeaders(token),
  });
  return response.data;
};

export const updateAdminServiceAliases = async (token, aliases) => {
  const response = await adminApi.patch("/service-aliases", { aliases }, {
    headers: buildAdminAuthHeaders(token),
  });
  return response.data;
};

export const fetchAdminContainerLogs = async (
  service,
  token,
  { tail = 200, sinceSeconds = null, timestamps = true } = {},
) => {
  const params = {
    tail,
    timestamps,
  };
  if (sinceSeconds) params.since_seconds = sinceSeconds;

  const response = await adminApi.get(`/containers/${service}/logs`, {
    headers: buildAdminAuthHeaders(token),
    params,
  });
  return response.data;
};

export const restartAdminContainer = async (service, token, confirmation) => {
  const response = await adminApi.post(
    `/containers/${service}/restart`,
    { confirmation },
    { headers: buildAdminAuthHeaders(token) },
  );
  return response.data;
};

export const fetchAdminHealthcheckTargets = async (token) => {
  const response = await adminApi.get('/healthchecks/targets', {
    headers: buildAdminAuthHeaders(token),
  });
  return response.data;
};

export const createAdminHealthcheckTarget = async (token, payload) => {
  const response = await adminApi.post('/healthchecks/targets', payload, {
    headers: buildAdminAuthHeaders(token),
  });
  return response.data;
};

export const updateAdminHealthcheckTarget = async (targetId, token, payload) => {
  const response = await adminApi.patch(`/healthchecks/targets/${targetId}`, payload, {
    headers: buildAdminAuthHeaders(token),
  });
  return response.data;
};

export const runAdminHealthcheckTargetNow = async (targetId, token) => {
  const response = await adminApi.post(`/healthchecks/targets/${targetId}/check`, {}, {
    headers: buildAdminAuthHeaders(token),
  });
  return response.data;
};

export const fetchAdminIncidents = async (token, limit = 100) => {
  const response = await adminApi.get('/incidents', {
    headers: buildAdminAuthHeaders(token),
    params: { limit },
  });
  return response.data;
};

export const forecastAPI = {
  getForecasts: async (offset = 0, limit = 100, filters = {}) => {
    const params = { offset, limit, include_automated: true };
    if (filters.title) params.title = filters.title;
    if (filters.lang) params.lang = filters.lang;
    const response = await api.get('/telegram/forecasts', { params });
    if (response._isAuthError) return [];
    return response.data;
  },

  createForecast: async (forecastData) => {
    const response = await api.post('/telegram/forecasts', forecastData);
    if (response._isAuthError) return null;
    return response.data;
  },

  updateForecast: async (id, forecastData) => {
    const response = await api.put(`/telegram/forecasts/${id}`, forecastData);
    if (response._isAuthError) return null;
    return response.data;
  },

  deleteForecast: async (id) => {
    const response = await api.delete(`/telegram/forecasts/${id}`);
    if (response._isAuthError) return null;
    return response.data;
  },

  getForecast: async (id) => {
    const response = await api.get(`/telegram/forecasts/${id}`);
    if (response._isAuthError) return null;
    return response.data;
  },

  getForecastImageUrl: (id, cacheBust = null) => {
    const base = `${API_BASE_URL}/telegram/forecasts/${id}/image`;
    return cacheBust ? `${base}?v=${cacheBust}` : base;
  },

  generateForecastImage: async (forecastData) => {
    const response = await api.post('/telegram-bot/generate-and-send-forecast', forecastData);
    if (response._isAuthError) return null;
    return response.data;
  },

  scheduleManualForecast: async (scheduleData) => {
    const response = await api.post('/telegram-bot/schedule-manual-forecast', scheduleData);
    if (response._isAuthError) return null;
    return response.data;
  },
};

export const setTimeframeSide = async (
  side,
  symbol,
  dest,
  strategy,
  timeframe,
  account = null,
  sectype = null,
) => {
  try {
    const data = {
      side: side,
      symbol: symbol,
      broker: dest,
      strategy: strategy,
      timeframe: timeframe,
      account: account,
      sectype: sectype,
    };

    const response = await api.put("/user/set-timeframe-side", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setJ2F = async (
  isJ2F,
  symbol,
  dest,
  timeframe,
  strategy,
  account = null,
  sectype = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: dest,
      timeframe: timeframe,
      account: account,
      strategy: strategy,
      sectype: sectype,
      isJ2F: isJ2F,
    };

    const response = await api.put("/user/set-j2f-02f", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setJoeRider = async (
  isJoeRider,
  symbol,
  dest,
  timeframe,
  strategy,
  account = null,
  sectype = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: dest,
      account: account,
      strategy: strategy,
      sectype: sectype,
      isJoeRider: isJoeRider,
      timeframe: timeframe,
    };

    const response = await api.put("/user/set-joe-rider", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setTimeframeMetaAutoresolve = async (
  status,
  symbol,
  dest,
  account = null,
  sectype = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: dest,
      account: account,
      sectype: sectype,
      status: status,
    };

    const response = await api.put("/user/set-timeframe-meta-autoresolve", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setTacticalStart = async (
  status,
  symbol,
  dest,
  timeframe,
  strategy,
  account = null,
  sectype = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: dest,
      account: account,
      sectype: sectype,
      status: status,
      timeframe: timeframe,
      strategy: strategy,
    };

    const response = await api.post("/user/set-tactical-start", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const setUpdown = async (
  status,
  symbol,
  dest,
  timeframe,
  strategy,
  account = null,
  sectype = null,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: dest,
      account: account,
      sectype: sectype,
      status: status,
      timeframe: timeframe,
      strategy: strategy,
    };

    const response = await api.post("/user/set-updown", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    return null;
  }
};

export const updateBlacklistStatus = async (
  broker,
  account,
  symbol,
  sectype,
  isBlackList,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      sectype: sectype,
      isBlackList: isBlackList,
    };
    const response = await api.put("/user/set-blacklist", data, {
      timeout: 10000,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: Cannot connect to DBCLIENT: ${error}`);
    throw error;
  }
};
export const toggleVirtualAccountSync = async (
  virtualAccountId,
  syncEnabled,
) => {
  try {
    const response = await api.put("/brokers/virtual-accounts/toggle-sync", {
      virtual_account_id: virtualAccountId,
      sync_enabled: syncEnabled,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error toggling sync for virtual account:", error);
    return null;
  }
};

export const fetchVirtualAccounts = async () => {
  try {
    const response = await api.get("/brokers/virtual-accounts");
    if (response._isAuthError) return [];
    return response.data.virtual_accounts;
  } catch (error) {
    console.error("Error fetching virtual accounts:", error);
    return [];
  }
};

export const createVirtualAccount = async (
  name,
  accountIds,
  sourceAccountId,
) => {
  try {
    const response = await api.post("/brokers/link-virtual", {
      name,
      account_ids: accountIds,
      source_account_id: sourceAccountId,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error creating virtual account:", error);
    throw error;
  }
};

export const fetchSignalHistory = async (
  account,
  broker,
  symbol,
  limit = 21,
  offset = 0,
) => {
  try {
    const response = await api.get("/user/history", {
      params: {
        account,
        broker,
        symbol,
        limit,
        offset,
      },
    });
    if (response._isAuthError) return { signals: [], total: 0 };
    return response.data;
  } catch (error) {
    console.error("Error fetching signal history:", error);
    return { signals: [], total: 0 };
  }
};

export const fetchHitHistory = async (account, broker, symbol, timeframeMeta) => {
  try {
    const response = await api.get("/user/hit-history", {
      params: { account, broker, symbol, timeframe_meta: timeframeMeta || undefined },
    });
    if (response._isAuthError) return { timeframes: [], hits: [] };
    return response.data;
  } catch (error) {
    console.error("Error fetching hit history:", error);
    return { timeframes: [], hits: [] };
  }
};

export const fetchHitFavorites = async (account, broker, symbol) => {
  try {
    const response = await api.get("/user/hit-favorites", { params: { account, broker, symbol } });
    if (response._isAuthError) return [];
    return response.data.favorites || [];
  } catch (error) {
    console.error("Error fetching hit favorites:", error);
    return [];
  }
};

export const saveHitFavorites = async (account, broker, symbol, timeframes) => {
  try {
    const response = await api.put("/user/hit-favorites", { account, broker, symbol, timeframes });
    if (response._isAuthError) return null;
    return response.data.favorites;
  } catch (error) {
    console.error("Error saving hit favorites:", error);
    return null;
  }
};

// Dashboard Favorites: starred instruments with their starred timeframes' SC/TE/LT rows.
// Null on failure, so the page can tell "none starred" from "could not load".
export const fetchFavoritesDashboard = async () => {
  try {
    const response = await api.get("/user/favorites-dashboard");
    if (response._isAuthError) return null;
    return response.data.favorites || [];
  } catch (error) {
    console.error("Error fetching favorites dashboard:", error);
    return null;
  }
};

export const fetchHitLogs = async (account, broker, symbol, hitId) => {
  try {
    const response = await api.get("/user/hit-logs", {
      params: { account, broker, symbol, hit_id: hitId },
    });
    if (response._isAuthError) return { logs: null };
    return response.data;
  } catch (error) {
    console.error("Error fetching hit logs:", error);
    return { logs: null, error: true };
  }
};

export const fetchAllSignalHistory = async (
  account,
  broker,
  symbol,
  timeframe = null,
) => {
  try {
    let allSignals = [];
    let hasMore = true;
    let offset = 0;
    const limit = 100;

    while (hasMore) {
      const params = {
        account,
        broker,
        symbol,
        limit,
        offset,
      };

      if (timeframe) {
        params.timeframe = timeframe;
      }
      const response = await api.get("/user/history", { params });

      if (response._isAuthError) {
        return { signals: [], total: 0 };
      }

      const { signals } = response.data;

      if (signals && signals.length > 0) {
        allSignals = [...allSignals, ...signals];
        offset += signals.length;
        hasMore = signals.length === limit;
      } else {
        hasMore = false;
      }
    }

    return { signals: allSignals, total: allSignals.length };
  } catch (error) {
    console.error("Error fetching all signal history:", error);
    throw error;
  }
};

export const deleteVirtualAccount = async (virtualAccountId) => {
  try {
    const response = await api.delete(
      `/brokers/virtual-accounts/${virtualAccountId}`,
    );
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error deleting virtual account:", error);
    throw error;
  }
};

export const syncVirtualAccountAssets = async (virtualAccountId) => {
  try {
    const response = await api.post(
      `/brokers/virtual-accounts/${virtualAccountId}/sync`,
    );
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error syncing virtual account assets:", error);
    throw error;
  }
};

export const checkAssetCompatibility = async (virtualAccountId) => {
  try {
    const response = await api.get(
      `/brokers/virtual-accounts/${virtualAccountId}/compatibility`,
    );
    if (response._isAuthError) return { compatible: [], incompatible: [] };
    return response.data;
  } catch (error) {
    console.error("Error checking asset compatibility:", error);
    throw error;
  }
};

export const resetCounterAccuracy = async (
  symbol,
  broker,
  account,
  sectype,
  group,
) => {
  try {
    const response = await api.post("/user/reset-counter-accuracy", {
      symbol,
      broker,
      account,
      sectype,
      group,
    });

    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error resetting counter accuracy:", error);
    throw error;
  }
};

export const fetchLogsWithFilters = async (filters) => {
  try {
    const response = await api.post("/user/logs", filters);
    if (response._isAuthError) return { logs: [], total: 0 };
    return response.data;
  } catch (error) {
    if (error._isAuthError) return { logs: [], total: 0 };
    console.error("Error fetching logs:", error);
    return { logs: [], total: 0 };
  }
};

// ── Notifications feed (notification-service) ──────────────────────────────
export const fetchNotifications = async (filters = {}) => {
  try {
    const response = await api.post("/notifications/list", filters);
    if (response._isAuthError) return { notifications: [], total: 0 };
    return response.data;
  } catch (error) {
    if (error._isAuthError) return { notifications: [], total: 0 };
    console.error("Error fetching notifications:", error);
    return { notifications: [], total: 0 };
  }
};

export const fetchNotificationsUnreadCount = async (filters = {}) => {
  try {
    const response = await api.post("/notifications/unread-count", filters);
    if (response._isAuthError) return 0;
    return response.data?.count ?? 0;
  } catch (error) {
    if (error._isAuthError) return 0;
    console.error("Error fetching unread count:", error);
    return 0;
  }
};

export const markNotificationsRead = async (ids) => {
  try {
    await api.post("/notifications/read", { ids });
  } catch (error) {
    if (!error._isAuthError) console.error("Error marking read:", error);
  }
};

export const flagNotifications = async (ids, flagged = true) => {
  try {
    await api.post("/notifications/flag", { ids, flagged });
  } catch (error) {
    if (!error._isAuthError) console.error("Error flagging notifications:", error);
  }
};

export const deleteNotifications = async (ids) => {
  try {
    const response = await api.post("/notifications/delete", { ids });
    if (response._isAuthError) return null;
    return response.data || { status: "ok" };
  } catch (error) {
    if (!error._isAuthError) console.error("Error deleting notifications:", error);
    return null;
  }
};

export const clearNotifications = async (filters = {}) => {
  try {
    const response = await api.post("/notifications/clear", filters);
    if (response._isAuthError) return null;
    return response.data || { status: "ok" };
  } catch (error) {
    if (!error._isAuthError) console.error("Error clearing notifications:", error);
    return null;
  }
};

export const toggleStrengthFilter = async (
  symbol,
  broker,
  account,
  sectype,
  filterName,
) => {
  try {
    const data = {
      symbol: symbol,
      broker: broker,
      account: account,
      sectype: sectype,
      filter_name: filterName,
    };

    console.log("Toggling strength filter:", data);

    const response = await api.post("/user/toggle-strength-filter", data, {
      timeout: 10000,
    });

    if (response._isAuthError) {
      console.error("Auth error when toggling strength filter");
      return null;
    }

    console.log("Strength filter toggle response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when toggling strength filter: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const getSyntheticAssets = async (broker = null, account = null) => {
  try {
    const params = {};
    if (broker) params.broker = broker;
    if (account) params.account = account;

    const response = await api.get("/user/synthetic-assets", { params });

    if (response._isAuthError) {
      console.error("Auth error when fetching synthetic assets");
      return [];
    }

    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when fetching synthetic assets: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return [];
  }
};

export const updateSyntheticAsset = async (assetData) => {
  try {
    console.log("Updating synthetic asset:", assetData);

    const response = await api.put("/user/synthetic-assets/update", assetData, {
      timeout: 10000,
    });

    if (response._isAuthError) {
      console.error("Auth error when updating synthetic asset");
      return null;
    }

    console.log("Synthetic asset update response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when updating synthetic asset: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const transferSyntheticAsset = async (transferData) => {
  try {
    console.log("Transferring synthetic asset:", transferData);

    const response = await api.post(
      "/user/synthetic-assets/transfer",
      transferData,
      { timeout: 15000 },
    );

    if (response._isAuthError) {
      console.error("Auth error when transferring synthetic asset");
      return null;
    }

    console.log("Synthetic asset transfer response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when transferring synthetic asset: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const deleteSyntheticAsset = async (assetId) => {
  try {
    console.log("Deleting synthetic asset:", assetId);

    const response = await api.delete(`/user/synthetic-assets/${assetId}`, {
      timeout: 10000,
    });

    if (response._isAuthError) {
      console.error("Auth error when deleting synthetic asset");
      return null;
    }

    console.log("Synthetic asset deletion response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when deleting synthetic asset: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const getSyntheticAssetFields = async (assetId) => {
  try {
    const response = await api.get(`/user/synthetic-assets/${assetId}/fields`);

    if (response._isAuthError) {
      console.error("Auth error when fetching synthetic asset fields");
      return null;
    }

    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when fetching synthetic asset fields: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const getVirtualAccountSyncSettings = async (virtualAccountId) => {
  try {
    const response = await api.get(
      `/brokers/virtual-accounts/${virtualAccountId}/sync-settings`,
    );
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error fetching sync settings:", error);
    return null;
  }
};

export const updateVirtualAccountSyncSettings = async (
  virtualAccountId,
  settings,
) => {
  try {
    const response = await api.put(
      `/brokers/virtual-accounts/${virtualAccountId}/sync-settings`,
      settings,
    );
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error updating sync settings:", error);
    throw error;
  }
};

export const getVirtualAccountInstruments = async (virtualAccountId) => {
  try {
    const response = await api.get(
      `/brokers/virtual-accounts/${virtualAccountId}/instruments`,
    );
    if (response._isAuthError)
      return { enabled_instruments: [], available_instruments: [], failed: true };
    return response.data;
  } catch (error) {
    console.error("Error fetching virtual account instruments:", error);
    // An empty list means "nothing enabled"; `failed` tells a failed read apart.
    return { enabled_instruments: [], available_instruments: [], failed: true };
  }
};

export const updateVirtualAccountInstruments = async (
  virtualAccountId,
  instruments,
) => {
  try {
    const response = await api.put(
      `/brokers/virtual-accounts/${virtualAccountId}/instruments`,
      instruments,
    );
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error("Error updating virtual account instruments:", error);
    throw error;
  }
};

export const createSyntheticAsset = async (assetData) => {
  try {
    console.log("Creating synthetic asset:", assetData);

    const response = await api.post(
      "/user/synthetic-assets/create",
      assetData,
      { timeout: 10000 },
    );

    if (response._isAuthError) {
      console.error("Auth error when creating synthetic asset");
      return null;
    }

    console.log("Synthetic asset creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when creating synthetic asset: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const createSynthPair = async (pairData) => {
  try {
    console.log("Creating synthetic pair:", pairData);

    const response = await api.post("/user/synth-pairs/create", pairData, {
      timeout: 10000,
    });

    if (response._isAuthError) {
      console.error("Auth error when creating synthetic pair");
      return null;
    }

    console.log("Synthetic pair creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when creating synthetic pair: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const deleteSynthPair = async (pairId) => {
  try {
    console.log("Deleting synthetic pair:", pairId);
    const response = await api.delete(`/user/synth-pairs/${pairId}`, {
      timeout: 10000,
    });
    if (response._isAuthError) {
      console.error("Auth error when deleting synthetic pair");
      return null;
    }
    console.log("Synthetic pair deletion response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when deleting synthetic pair: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const updateSynthPair = async (pairId, pairData) => {
  try {
    console.log("Updating synthetic pair:", pairId, pairData);

    const response = await api.put(`/user/synth-pairs/${pairId}`, pairData, {
      timeout: 10000,
    });

    if (response._isAuthError) {
      console.error("Auth error when updating synthetic pair");
      return null;
    }

    console.log("Synthetic pair update response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when updating synthetic pair: ${error}`,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

export const getSynthPairs = async (broker = null, account = null) => {
  try {
    const params = {};
    if (broker) params.broker = broker;
    if (account) params.account = account;

    const response = await api.get("/user/synth-pairs", {
      params,
      timeout: 10000,
    });

    if (response._isAuthError) {
      console.error("Auth error when fetching synth pairs");
      return [];
    }

    return response.data || [];
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when fetching synth pairs: ${error}`,
    );
    return [];
  }
};

export const setPriority = async (id, priority, data) => {
  try {
    console.log("Setting priority for id:", id, "priority:", priority);

    const response = await api.post(
      "/user/set-priority",
      {},
      {
        params: {
          id: id,
          priority: priority,
        },
        timeout: 10000,
      },
    );

    if (response._isAuthError) {
      console.error("Auth error when setting priority");
      return null;
    }

    console.log("Priority set successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      `Exception: Cannot connect to backend when set priority:`,
      error,
    );
    if (error.response && error.response.data) {
      console.error("Server response:", error.response.data);
    }
    return null;
  }
};

// --- Scanner library: one instrument per symbol, folders on top of it. ---

export const fetchScannerLibrary = async () => {
  const response = await api.get("/scanner/library");
  if (response._isAuthError) return null;
  return response.data;
};

export const createScannerFolder = async (name) => {
  const response = await api.post("/scanner/folders", { name });
  if (response._isAuthError) return null;
  return response.data;
};

export const renameScannerFolder = async (folderId, name) => {
  const response = await api.patch(`/scanner/folders/${folderId}`, { name });
  if (response._isAuthError) return null;
  return response.data;
};

export const deleteScannerFolder = async (folderId) => {
  const response = await api.delete(`/scanner/folders/${folderId}`);
  if (response._isAuthError) return null;
  return response.data;
};

// Resolves to { needsParent: true, accounts } when the instrument sits on several
// accounts: the seed overwrites the others, so the operator picks the source.
export const addInstrumentToFolder = async (folderId, instrument, parentAccount = null) => {
  try {
    const response = await api.post(`/scanner/folders/${folderId}/items`, {
      broker: instrument.broker,
      symbol: instrument.symbol,
      sectype: instrument.sectype || "",
      parent_account: parentAccount,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 409 && detail?.reason === "parent_required") {
      return { needsParent: true, accounts: detail.accounts };
    }
    console.error(`Exception: cannot add instrument to folder: ${error}`);
    return null;
  }
};

export const removeInstrumentFromFolder = async (folderId, instrument) => {
  const response = await api.post(`/scanner/folders/${folderId}/items/remove`, {
    broker: instrument.broker,
    symbol: instrument.symbol,
    sectype: instrument.sectype || "",
  });
  if (response._isAuthError) return null;
  return response.data;
};

// Manual entry into the library: the row is created scanner-side (MONITOR unless
// an account is named) with a zero position, exactly like a fresh scanner find.
export const createScannerInstrument = async (instrument) => {
  try {
    const response = await api.post("/scanner/instruments", instrument);
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    return { error: error.response?.data?.detail || "Could not create the instrument" };
  }
};

// Removes every account row behind the card. The server refuses while the
// instrument is trading.
export const deleteScannerInstrument = async (instrument) => {
  try {
    const response = await api.post("/scanner/instruments/delete", {
      broker: instrument.broker,
      symbol: instrument.symbol,
      sectype: instrument.sectype || "",
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    return { error: error.response?.data?.detail || "Could not delete the instrument" };
  }
};

// Sectype / currency / exchange of an IB security on every account behind the
// card. Resolves to { conflicts } when something already sits at the new sectype:
// the operator confirms, and the call is repeated with overwrite. With transfer
// the synthetics behind the card move to securities once edited.
export const editScannerInstrument = async (instrument, changes, overwrite = false, transfer = false) => {
  try {
    const response = await api.post("/scanner/instruments/edit", {
      broker: instrument.broker,
      symbol: instrument.symbol,
      sectype: instrument.sectype || "",
      new_sectype: changes.sectype || "",
      new_currency: changes.currency || "",
      new_exchange: changes.exchange || "",
      new_side: changes.side || "",
      overwrite,
      transfer,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 409 && detail?.reason === "target_exists") {
      return { conflicts: detail.conflicts, sectype: detail.sectype };
    }
    if (detail?.reason === "not_transferable") {
      return { error: `Cannot transfer: ${detail.missing.join(", ")} missing on ${detail.account}` };
    }
    return { error: detail || "Could not edit the instrument" };
  }
};

// Both flags are set for the whole instrument — every account behind the card —
// and the server keeps them consistent: blacklisting parks an instrument, and
// switching a blacklisted one on takes it off the list.
export const setInstrumentBlacklist = async (instrument, value) => {
  try {
    const response = await api.post("/scanner/blacklist", {
      broker: instrument.broker,
      symbol: instrument.symbol,
      sectype: instrument.sectype || "",
      value,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: cannot change the blacklist flag: ${error}`);
    return { error: error.response?.data?.detail || "Blacklist change failed" };
  }
};

export const setInstrumentActive = async (instrument, value) => {
  try {
    const response = await api.post("/scanner/active", {
      broker: instrument.broker,
      symbol: instrument.symbol,
      sectype: instrument.sectype || "",
      value,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: cannot change the active flag: ${error}`);
    return { error: error.response?.data?.detail || "Active change failed" };
  }
};

// The virtual accounts, read as a folder layout: nothing is written until the
// operator confirms the plan in the preview.
export const fetchAutoFolderPlan = async () => {
  try {
    const response = await api.get("/scanner/auto-folders/plan");
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: cannot build the auto-folder plan: ${error}`);
    return null;
  }
};

// `folders` is the resolved plan — [{ name, items: [{ broker, symbol, sectype,
// parent_account }] }] — so conflicts are settled before the server sees it.
export const applyAutoFolderPlan = async (folders) => {
  try {
    const response = await api.post("/scanner/auto-folders/apply", { folders });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    console.error(`Exception: cannot apply the auto-folder plan: ${error}`);
    return null;
  }
};

// `account` may be a single account or the whole desired trading set — the
// server treats a list as absolute and demotes whatever is not in it.
export const promoteInstrumentToTrading = async (instrument, account) => {
  try {
    const response = await api.post("/scanner/promote", {
      broker: instrument.broker,
      symbol: instrument.symbol,
      sectype: instrument.sectype || "",
      account,
    });
    if (response._isAuthError) return null;
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 409 && detail?.reason === "synthetic_not_transferred") {
      return { needsTransfer: true, syntheticId: detail.synthetic_id };
    }
    console.error(`Exception: cannot promote instrument: ${error}`);
    return { error: detail || "Promotion failed" };
  }
};

export default api;
